import React, { useMemo, useState, KeyboardEvent } from 'react';
import {
  Box,
  Flex,
  Icon,
  Text,
  TextInput,
  ColorsV2,
  spaces,
  useThemeColors,
} from '@electricjs/arc';
import { FilterDropdown } from '@common';
import { StyledFilterOption } from './SelectFilter';

export type SearchOption = {
  id: string;
  value: string;
  text: string;
  disabled?: boolean;
};

type Props = {
  options: SearchOption[];
  selectedValues: string[];
  /** Selection logic: the parent decides to add or remove */
  onSelect: (value: string, alreadySelected: boolean) => void;
  /** Filter name */
  filterName: string;
  /** Placeholder */
  placeholder?: string;
  emptyText?: string;

  /** Maximum height of the scrollable options */
  maxHeight?: string | number;

  /** Controls the “badge”/visual state of something being checked */
  someOptionIsChecked?: boolean;

  /** (Optional) Props for the dropdown content box */
  dropdownContentProps?: {
    padding?: string;
    minWidth?: string;
    width?: string;
    maxWidth?: string | number;
  };
  /** Optional input id */
  inputId?: string;
  /** Initial query */
  initialQuery?: string;
};

const SearchableListFilter: React.FC<Props> = ({
  options,
  selectedValues,
  onSelect,
  filterName,
  placeholder = 'Search here',
  emptyText = 'No results found',
  maxHeight = '200px',
  someOptionIsChecked = selectedValues.length > 0,
  dropdownContentProps = {
    padding: '0',
    minWidth: 'fit-content',
    width: 'max-content',
    maxWidth: spaces.space_9xl,
  },
  inputId = 'searchable-list-filter-input',
  initialQuery = '',
}) => {
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [dropdownKey, setDropdownKey] = useState(0);

  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.text.toLowerCase().includes(q));
  }, [options, searchTerm]);

  const handleChoose = (opt: SearchOption) => {
    if (opt.disabled) return;

    // Check if the option is already selected
    const alreadySelected = selectedValues.includes(opt.value);

    // Call the onSelect callback with the option value and whether it is already selected
    onSelect(opt.value, alreadySelected);
    setSearchTerm(''); // clear search
    setDropdownKey(k => k + 1); // force dropdown close
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      // Handle the Enter key press
      handleChoose(filteredOptions[0]);
    }
  };
  const borderColor = useThemeColors(ColorsV2.BORDER);

  const dropdownContent = [
    // Search input
    <Box
      key="search-input"
      p={spaces.space_m_rem}
      borderBottom={`1px solid ${borderColor}`}>
      <TextInput
        id={inputId}
        placeholder={placeholder}
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        onKeyDownCapture={onInputKeyDown}
        icon="search"
        iconPosition="left"
      />
    </Box>,

    // Scrollable list
    <Box key="options-container" maxHeight={maxHeight} overflowY="auto">
      {filteredOptions.length > 0 ? (
        filteredOptions.map(option => {
          const showIcon = selectedValues.includes(option.value);
          return (
            <StyledFilterOption
              key={option.id}
              id={`filter-${option.id}`}
              onClick={() => handleChoose(option)}
              disabled={option.disabled}
              p={`${spaces.space_s_rem} ${spaces.space_m_rem}`}
              alignItems="center"
              role="option"
              aria-selected={showIcon}>
              {showIcon && <Icon icon="check" intent="primary" size="small" />}
              <Text
                variant="body-2"
                pl={
                  someOptionIsChecked && !showIcon
                    ? spaces.space_l_rem
                    : spaces.space_m_rem
                }>
                {option.text}
              </Text>
            </StyledFilterOption>
          );
        })
      ) : (
        <Flex p={spaces.space_m_rem} justifyContent="center">
          <Text variant="body-2" color={ColorsV2.TEXT}>
            {emptyText}
          </Text>
        </Flex>
      )}
    </Box>,
  ];

  return (
    <FilterDropdown
      key={dropdownKey}
      someOptionIsChecked={someOptionIsChecked}
      dropdownContent={dropdownContent}
      dropdownContentProps={dropdownContentProps}
      filterName={filterName}
      size="small"
    />
  );
};

export default SearchableListFilter;
